/**
 * Invite acceptance API (task 635).
 *
 * The `/accept-invite` landing route calls this after the recipient has signed
 * in through Keycloak. It posts the invitation token to the innovate-service
 * accept proxy, which forwards it (with the caller's Keycloak subject) to AMS's
 * token-scoped accept endpoint (`POST /api/account-invitations/accept`).
 *
 * The account is derived server-side from the token + the caller's scope; the
 * client only carries the token. Kept in its own module (not folded into
 * services/team.ts) so it stays independent of the Team page's proxy work.
 *
 * ─── Backend status (2026-08-20) ────────────────────────────────────────────
 * The innovate-service accept proxy is task 636's domain and may not exist yet:
 *   - POST /api/account/invitations/accept   { token }
 * Until it lands the call 404s and the landing page shows its neutral
 * "couldn't complete" state. The AMS acceptance side (this task) is complete;
 * the provisioning branch already joins a brand-new invited user by verified
 * email, so acceptance still succeeds even before this proxy exists.
 *
 * Reuses the shared axios instance so it inherits bearer-token injection and the
 * 401-refresh interceptor — it does NOT create a second client.
 */

import api from './api';
import type { SuccessResponse } from '../types';

/** Machine codes AMS returns on the error path (surfaced to the landing page). */
export type AcceptErrorCode =
  | 'INVITATION_NOT_FOUND'
  | 'EMAIL_MISMATCH'
  | 'EXPIRED'
  | 'REVOKED'
  | 'ALREADY_ACCEPTED'
  | 'ACCOUNT_NOT_FOUND'
  | 'USER_NOT_FOUND';

export interface AcceptInvitationResult {
  /** The updated invitation record (status: accepted). */
  invitation?: unknown;
  /** The account membership that was created or kept. */
  membership?: unknown;
  /** True on an idempotent repeat (the invite was already accepted by you). */
  alreadyAccepted?: boolean;
}

export const inviteAcceptApi = {
  /**
   * POST /api/account/invitations/accept — complete acceptance for the signed-in
   * User. Resolves on success; rejects with the axios error (whose
   * `response.data.code` is one of AcceptErrorCode) otherwise.
   */
  accept: (token: string) =>
    api.post<SuccessResponse<AcceptInvitationResult>>('/account/invitations/accept', { token }),

  /**
   * GET /api/account/invitations/lookup?token=… — the PUBLIC, token-scoped
   * lookup that lets the landing page name the inviting account before the
   * visitor has any login at all (task 730).
   *
   * It reveals only what the invitation email the visitor is holding already
   * contained, which is what makes it safe to serve unauthenticated. The shared
   * axios instance is reused deliberately: with no session there is simply no
   * bearer header to inject, and the call still inherits the app's base URL,
   * logging and error shaping rather than becoming a bare `fetch`.
   */
  lookup: (token: string) =>
    api.get<SuccessResponse<InvitationLookup>>(
      `/account/invitations/lookup?token=${encodeURIComponent(token)}`
    ),
};

// ---------------------------------------------------------------------------
// Public lookup contract (task 730 / 731)
// ---------------------------------------------------------------------------

/**
 * Where the invitation stands. Anything other than `pending` is a dead end the
 * landing page explains in place — before any auth friction, which is the whole
 * point of doing the lookup pre-auth.
 */
export type InvitationLookupStatus = 'pending' | 'expired' | 'revoked' | 'accepted';

export interface InvitationLookup {
  /** The inviting account's display name — the "who invited me" answer. */
  accountName: string;
  /** The invited address. The invitation is keyed on it; see the landing copy. */
  email: string;
  /** The account role the invitation grants, e.g. `admin`. */
  role: string;
  status: InvitationLookupStatus;
  /** ISO timestamp, or null when the backend does not carry one. */
  expiresAt?: string | null;
}
